import type { Catalogue } from "../data/schema";
import { fill, messages, plural } from "../i18n/index";
import {
  bodySubtitle,
  buildBodyTree,
  groupFor,
  localizeNames,
  searchBodies,
  splitGroup,
  type TreeNode,
} from "./bodies";
import { button, localize } from "./dom";
import type { Store, ViewState } from "./store";

export interface BodiesTabDeps {
  bodies: Catalogue;
  store: Store;
  onSelect(id: string): void;
}

export interface BodiesTab {
  focusSearch(): void;
  /** Clears the search and expands a body's moon group. */
  reveal(id: string): void;
}

function spacer(): HTMLSpanElement {
  const s = document.createElement("span");
  s.className = "body-spacer";
  return s;
}

export function mountBodiesTab(
  panel: HTMLElement,
  { bodies, store, onSelect }: BodiesTabDeps,
): BodiesTab {
  // names, and with them the tree, follow the language
  let named = bodies;
  let tree: TreeNode[] = [];
  const expanded = new Set<string>([groupFor(bodies, store.state.focus)]);

  const search = document.createElement("input");
  search.type = "search";
  search.className = "bodies-search";
  search.autocomplete = "off";
  search.spellcheck = false;
  const list = document.createElement("div");
  list.className = "bodies-list";
  // the list scrolls on its own so the search field stays in reach
  panel.classList.add("bodies-tab");
  panel.append(search, list);

  /** Replaces the list rows, keeping keyboard focus on the equivalent element if it was in the list. */
  const setRows = (rows: HTMLElement[]): void => {
    const active = document.activeElement;
    const key =
      active instanceof HTMLElement && list.contains(active)
        ? active.dataset.caret !== undefined
          ? `[data-caret="${CSS.escape(active.dataset.caret)}"]`
          : active.dataset.id !== undefined
            ? `[data-id="${CSS.escape(active.dataset.id)}"]`
            : null
        : null;
    list.replaceChildren(...rows);
    if (key) list.querySelector<HTMLElement>(key)?.focus();
  };

  const bodyButton = (
    id: string,
    name: string,
    color: string,
    note: string,
    description: string,
    mark: [number, number] | null,
  ): HTMLButtonElement => {
    const b = button("", () => onSelect(id), "body-name");
    b.dataset.id = id;
    b.setAttribute("aria-label", description ? `${name}, ${description}` : name);
    if (id === store.state.focus) {
      b.classList.add("current");
      b.setAttribute("aria-current", "true");
    }
    const dot = document.createElement("span");
    dot.className = "body-dot";
    dot.style.background = color;
    const label = document.createElement("span");
    label.className = "body-label";
    if (mark) {
      const m = document.createElement("mark");
      m.textContent = name.slice(mark[0], mark[0] + mark[1]);
      label.append(name.slice(0, mark[0]), m, name.slice(mark[0] + mark[1]));
    } else label.textContent = name;
    b.append(dot, label);
    if (note) {
      const n = document.createElement("span");
      n.className = "body-note";
      n.textContent = note;
      b.append(n);
    }
    return b;
  };

  const renderTree = (): void => {
    const { lang } = store.state;
    const m = messages(lang).bodies;
    const rows: HTMLElement[] = [];
    const add = (node: TreeNode, depth: number): void => {
      const row = document.createElement("div");
      row.className = "body-row";
      row.style.setProperty("--depth", String(depth));
      const moons = node.children.length;
      if (moons > 0) {
        const open = expanded.has(node.id);
        const caret = button(
          open ? "▾" : "▸",
          () => {
            if (open) expanded.delete(node.id);
            else expanded.add(node.id);
            renderTree();
          },
          "body-caret",
        );
        caret.dataset.caret = node.id;
        caret.setAttribute("aria-expanded", String(open));
        caret.setAttribute(
          "aria-label",
          fill(open ? m.hideMoons : m.showMoons, { name: node.name }),
        );
        row.append(caret);
      } else row.append(spacer());
      row.append(
        bodyButton(
          node.id,
          node.name,
          node.color,
          moons > 0 ? String(moons) : "",
          moons > 0 ? plural(lang, m.moons, moons) : "",
          null,
        ),
      );
      rows.push(row);
      if (moons === 0 || !expanded.has(node.id)) return;
      const { shown, more } = splitGroup(node);
      for (const child of shown) add(child, depth + 1);
      // a focused moon beyond the cap still gets its highlighted row
      const hidden = node.children.slice(shown.length).find((c) => c.id === store.state.focus);
      if (hidden) add(hidden, depth + 1);
      const rest = more - (hidden ? 1 : 0);
      if (rest > 0)
        rows.push(button(fill(m.more, { count: rest }), () => search.focus(), "body-more"));
    };
    for (const node of tree) add(node, 0);
    setRows(rows);
  };

  const renderResults = (query: string): void => {
    const { lang } = store.state;
    const hits = searchBodies(named, query);
    if (hits.length === 0) {
      const empty = document.createElement("p");
      empty.className = "bodies-empty";
      empty.textContent = fill(messages(lang).bodies.noMatch, { query: query.trim() });
      setRows([empty]);
      return;
    }
    setRows(
      hits.map((hit) => {
        const row = document.createElement("div");
        row.className = "body-row";
        const subtitle = bodySubtitle(hit.kind, hit.parent, lang);
        row.append(
          spacer(),
          bodyButton(
            hit.id,
            hit.name,
            hit.color,
            subtitle,
            subtitle,
            hit.start >= 0 ? [hit.start, hit.length] : null,
          ),
        );
        return row;
      }),
    );
  };

  const render = (): void => {
    if (search.value.trim() === "") renderTree();
    else renderResults(search.value);
  };

  localize(store, (m, lang) => {
    named = localizeNames(bodies, lang);
    tree = buildBodyTree(named);
    search.placeholder = fill(m.bodies.search, { count: bodies.length });
    search.setAttribute("aria-label", m.bodies.searchLabel);
    if (store.state.menuOpen && store.state.menuTab === "bodies") render();
  });

  search.addEventListener("input", render);
  search.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && search.value !== "") {
      // first Esc clears; the menu closes on the next one
      search.value = "";
      render();
      e.stopPropagation();
      e.preventDefault();
    } else if (e.key === "Enter" && search.value.trim() !== "") {
      list.querySelector<HTMLButtonElement>(".body-name")?.click();
      e.preventDefault();
    } else if (e.key === "ArrowDown") {
      list.querySelector<HTMLButtonElement>("button")?.focus();
      e.preventDefault();
    }
  });

  let lastFocus = store.state.focus;
  let wasVisible = false;
  store.subscribe((s: ViewState) => {
    const visible = s.menuOpen && s.menuTab === "bodies";
    const focusChanged = s.focus !== lastFocus;
    if (focusChanged) {
      lastFocus = s.focus;
      expanded.add(groupFor(bodies, s.focus));
    }
    if (visible && !wasVisible) {
      render();
      // on a phone the focused body is often below the fold of the sheet
      list.querySelector(".body-name.current")?.scrollIntoView({ block: "center" });
    } else if (visible && focusChanged) render();
    wasVisible = visible;
  });

  return {
    focusSearch() {
      search.focus();
      search.select();
    },
    reveal(id) {
      expanded.add(id);
      search.value = "";
      render();
      list.querySelector(`[data-id="${CSS.escape(id)}"]`)?.scrollIntoView({ block: "nearest" });
    },
  };
}
